/* EVERLA · Welcome pop-up (sections/everla-welcome.liquid).
 *
 * The signup is Shopify's native customer form. This script never submits it and never calls
 * preventDefault on it: Shopify's hCaptcha binder owns the submit (it adds its token, then posts).
 * Shopify redirects back with ?customer_posted=true#EverlaWelcomeForm and we open the success state
 * ("check your inbox"). No code is shown or applied here: the welcome email sends a personal WEL15-XXXXXX code.
 *
 * Opens: after the delay or the scroll depth (whichever first, never before 15 s), on desktop exit intent,
 * immediately for ?welcome / #welcome, and on click of any [data-everla-welcome-open]. Never for subscribers, within the
 * dismissal window, or in the theme editor unless the section is selected. Never when a personal welcome
 * code (WEL15-…) is already on this visitor (discount_code cookie or cart, e.g. the welcome email's
 * /discount/CODE link on another device: they are subscribed), and not for the rest of a session that
 * arrived from an email.
 * QA (no side effects): #welcome-success, #welcome-already. ?welcome=es / ?welcome=en force the language.
 * API: window.EverlaWelcome.open('form' | 'success' | 'already'), window.EverlaWelcome.close(). An API open is the page
 * opening it, not the shopper, so it gets the same grace as an automatic open.
 *
 * The classic pop-up (2026-10-08, Diego: "I don't like the popup window for the 15% ... EVERY company does it"):
 * - Every open is the centred dialog: the page dims, stops scrolling and goes inert behind it; focus is trapped
 *   inside and handed back on close. It closes with the x, a tap outside, Escape or "No thanks". Computers: photo
 *   left, form right. Phones: a centred card with a short photo band on top; while the email field has focus (the
 *   keyboard is up) it sits at the top of the visible area and drops the band, eyebrow and text.
 * - Automatic opens (15 s timer, scroll depth, desktop exit intent) still put shopping first: never within 20 s of a
 *   touch of the buy box (product form, sticky bar, card "Add to cart", quick look), never within 1.5 s of a tap or a
 *   scroll, and never while the shopper is at the buy box (phones: the product form on screen; computers: the
 *   pointer resting on it). For GRACE ms after an automatic open the dimmed backdrop takes no taps: a tap already on
 *   its way to the page lands on the page, and one on Add to cart closes the pop-up and adds (one tap, nothing
 *   swallowed; also when the card now covers that Add to cart, unless the tap is on the x or "No thanks"). The card
 *   itself (x, field, button, "No thanks") takes taps from its first frame. Only after the grace
 *   does the page go inert. Opens the shopper asked for (the "15% off" tab, a ?welcome link) are modal at once.
 *   (2026-10-08: the first grace was 0.9 s on the whole overlay; a quick tap on the x went through to the photo link.)
 * - Shown once: an automatic or ?welcome open counts as seen, and every close starts the cooldown (dismiss_days,
 *   7 by default). The "15% off" tab reopens it meanwhile, and hides while it would sit on an Add to cart.
 * - ?welcome (Club ads) opens it at once. The form, copy, hCaptcha and track() are unchanged.
 *
 * Footer "Join" sign-ups count too (2026-10-08): both real sign-ups of Oct 6-7 came through the footer form
 * (form[data-ev-news], #EvNewsletter), which never sent the Lead. Its submit now leaves the same pending note
 * (form: 'footer'), and its ?customer_posted=true#EvNewsletter return publishes the same 'everla:welcome_signup'
 * event with source 'footer' (the Meta Lead custom pixel reads it as content_category). Same rule as the pop-up:
 * one Lead per real submit in this tab; a revisited or hand-made success URL is not counted.
 */
(() => {
  'use strict';
  if (window.EverlaWelcome && window.EverlaWelcome.loaded) return; // the editor may re-run section scripts

  const KEY = { sub: 'evw_subscribed', dis: 'evw_dismissed', teaser: 'evw_teaser_closed' };
  const SKEY = { pending: 'evw:pending', handled: 'evw:handled', lang: 'evw:lang', email: 'evw:email', promo: 'evw:promo' };
  const DAY = 864e5;
  const MIN_ENGAGED = 4000; // exit intent never fires in the first 4s on a page
  const AUTO_MIN = 15000; // the timer and the scroll trigger never open it before 15s on a page (Diego, 2026-10-08: 25s -> 15s)
  const BUY_QUIET = 20000; // ...nor within 20s of the shopper touching the buy box
  const CALM = 1500; // ...nor within 1.5s of a tap or a scroll, so it never appears under a finger
  const NEAR = 80; // px around the product form that still counts as "at the buy box"
  const GRACE = 350; // ms after an automatic open during which taps on the backdrop still go to the page (a tap on Add to cart adds)
  const PREWARM = 3000; // the photograph starts loading this long before the timer is due
  // The product page's buy box: the pop-up never opens while the shopper is at it.
  const BUYBOX = '[data-ev-pdp] form[data-ev-atc],[data-ev-pdp] .ev-addon';
  // Touching any of these counts as shopping (quiet period; in the grace moments it also closes the pop-up).
  const BUY = BUYBOX + ',.ev-sticky-atc,.ev-modal,[data-card-add],[data-quick-add],[data-quick-view],.ev-card .sw';
  // Add-to-cart controls the "15% off" tab must never sit on.
  const ATC = 'form[action*="/cart/add"] [type="submit"],.atc,.ev-sticky-atc .ev-btn,[data-card-add],[data-quick-add]';
  /* Also kept clear: the product price (2026-10-14: on phones the tab sat right on the $139 at the bottom of the first screen). */
  const KEEP_CLEAR = ATC + ',[data-ev-pdp] .price';
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
  // Query keys Shopify adds on a customer-form return. 'contact' also drops every contact[...] echo (it can carry the email).
  const FORM_PARAMS = ['customer_posted', 'form_type', 'form_key', 'utf8', 'contact'];
  const TYPING = 'form textarea,form input:not([type]),form input[type="text"],form input[type="email"],form input[type="tel"]';
  const NEWS = 'form[data-ev-news]'; // the footer "Join" newsletter form (sections/newsletter-v2.liquid)

  /* ---------- storage (every access guarded: private mode, blocked storage, previews) ---------- */
  const cookie = {
    get(k) { const m = document.cookie.match(new RegExp('(?:^|; )' + k + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : null; },
    set(k, v, days) { document.cookie = k + '=' + encodeURIComponent(v) + '; path=/; max-age=' + Math.round(days * 86400) + '; SameSite=Lax'; }
  };
  const local = {
    get(k) { let v = null; try { v = window.localStorage.getItem(k); } catch (e) { /* unavailable */ } return v !== null ? v : cookie.get(k); },
    set(k, v, days) { try { window.localStorage.setItem(k, v); } catch (e) { /* unavailable */ } try { cookie.set(k, v, days); } catch (e) { /* unavailable */ } }
  };
  const session = {
    get(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* unavailable */ } },
    del(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* unavailable */ } },
    works() { try { window.sessionStorage.setItem('evw:t', '1'); window.sessionStorage.removeItem('evw:t'); return true; } catch (e) { return false; } }
  };

  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const phone = window.matchMedia('(max-width: 749px)');
  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let app = null; // current instance (rebuilt on shopify:section:load)

  function create(root) {
    const $ = (s) => root.querySelector(s);
    const $$ = (s) => Array.prototype.slice.call(root.querySelectorAll(s));
    const cfgEl = root.parentNode.querySelector('[data-evw-config]');
    let cfg;
    try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { return null; }

    const dialog = $('.evw__dialog');
    const form = $('[data-evw-form]');
    const email = $('[data-evw-email]');
    const nameInput = $('[data-evw-name]');
    const submit = $('[data-evw-submit]');
    const teaser = root.parentNode.querySelector('[data-evw-teaser]');
    const disclaimerTpl = $('template[data-evw-disclaimer]');
    const disclaimerSlot = $('[data-evw-disclaimer-slot]');
    const cssLink = document.getElementById('EverlaWelcomeCSS');
    const ac = 'AbortController' in window ? new AbortController() : null;
    const listen = (el, type, fn, opts) => { if (el) el.addEventListener(type, fn, Object.assign({}, opts || {}, ac ? { signal: ac.signal } : {})); };
    const timers = [];
    const later = (fn, ms) => { timers.push(setTimeout(fn, ms)); };

    const state = { open: false, opening: false, view: 'form', trigger: 'auto', lastFocus: null, autoDone: false, started: Date.now(), preview: false, inerted: [], arming: false, armSeq: 0, lastBuy: 0, lastTouch: 0, px: -1, py: -1 };

    /* ---------- language ---------- */
    const url = new URL(window.location.href);
    const forced = (url.searchParams.get('welcome') || '').toLowerCase();
    const browserEs = /^es\b/i.test((navigator.languages && navigator.languages[0]) || navigator.language || '');
    let lang = forced === 'es' || forced === 'en' ? forced : session.get(SKEY.lang) || (browserEs ? 'es' : 'en');
    if (lang === 'es' && !cfg.spanish && forced !== 'es') lang = 'en';
    const t = (k) => ((cfg.i18n && cfg.i18n[lang] && cfg.i18n[lang][k]) || (cfg.i18n && cfg.i18n.en && cfg.i18n.en[k]) || '');

    function translate() {
      if (lang !== 'es' || !cfg.i18n || !cfg.i18n.es) return;
      const scopes = [root, teaser].filter(Boolean);
      scopes.forEach((scope) => {
        scope.setAttribute('lang', 'es');
        scope.querySelectorAll('[data-evw-t]').forEach((el) => { el.textContent = t(el.getAttribute('data-evw-t')); });
        scope.querySelectorAll('[data-evw-h]').forEach((el) => { el.innerHTML = t(el.getAttribute('data-evw-h')); });
        scope.querySelectorAll('[data-evw-aria]').forEach((el) => { el.setAttribute('aria-label', t(el.getAttribute('data-evw-aria'))); });
      });
    }

    /* hCaptcha notice. Shopify's binder skips its floating badge whenever a p[data-spam-detection-disclaimer]
       exists anywhere on the page, so ours is only in the DOM while the pop-up is open: the footer and contact
       forms keep their badge, and the pop-up (where the badge would sit on the sheet) shows the text instead. */
    function mountDisclaimer(on) {
      if (!disclaimerTpl || !disclaimerSlot) return;
      if (!on) { disclaimerSlot.textContent = ''; return; }
      if (disclaimerSlot.firstElementChild) return;
      const frag = disclaimerTpl.content.cloneNode(true);
      const p = frag.querySelector('p');
      if (p && lang === 'es' && cfg.i18n && cfg.i18n.es && cfg.i18n.es.disclaimer) p.innerHTML = cfg.i18n.es.disclaimer;
      disclaimerSlot.appendChild(frag);
    }

    /* Let Shopify's captcha bootstrap bind the form while the visitor reads, not on their first tap:
       it discards a submit that arrives before the form is bound. */
    const captchaOn = () => !!(window.Shopify && window.Shopify.captcha);
    const bound = () => !!(form && (form.hasAttribute('data-hcaptcha-bound') || form.hasAttribute('data-recaptcha-bound')));
    function warmCaptcha() {
      if (!form || designMode || bound() || !captchaOn()) return;
      try { form.dispatchEvent(new FocusEvent('focusin', { bubbles: true })); } catch (e) { /* old browser: binds on first focus */ }
    }

    /* The page behind the dialog: inert for keyboard, pointer and screen readers (aria-modal alone is not enough). */
    function setInert(on) {
      if (!on) { state.inerted.forEach((el) => { el.inert = false; }); state.inerted = []; return; }
      if (designMode || !('inert' in HTMLElement.prototype)) return;
      let node = root.closest('.shopify-section') || root;
      while (node && node.parentElement && node !== document.body) {
        Array.prototype.forEach.call(node.parentElement.children, (el) => {
          if (el === node || el.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(el.tagName)) return;
          if (!el.querySelector('form') && el.querySelector('iframe[src*="hcaptcha"][src*="frame=challenge"]')) return; // the captcha challenge must stay usable
          el.inert = true;
          state.inerted.push(el);
        });
        node = node.parentElement;
      }
    }

    /* Somewhere sensible for focus when there is no element to hand it back to. */
    function focusPage() {
      const main = document.getElementById('MainContent') || document.querySelector('main');
      if (!main) return;
      if (!main.hasAttribute('tabindex')) {
        main.setAttribute('tabindex', '-1');
        main.setAttribute('data-evw-focus', '');
        main.addEventListener('blur', () => { main.removeAttribute('tabindex'); main.removeAttribute('data-evw-focus'); }, { once: true });
      }
      main.focus({ preventScroll: true });
    }

    /* ---------- remembered state ---------- */
    const subscribed = () => !!local.get(KEY.sub);
    const dismissedAt = () => parseInt(local.get(KEY.dis) || '0', 10) || 0;
    const inCooldown = () => Date.now() - dismissedAt() < (cfg.days || 7) * DAY;
    function markSubscribed() { local.set(KEY.sub, String(Date.now()), 730); hideTeaser(); }

    /* Already holds a personal welcome code (WEL15-XXXXXX, one per subscriber): the welcome email's button goes
       through /discount/CODE, which sets a readable discount_code cookie and puts the code on the cart. They
       signed up (maybe on another device). cfg.code is the prefix; any code that starts with it counts. */
    const prefixLc = String(cfg.code || 'WEL15-').trim().toLowerCase();
    const isWelcomeCode = (c) => String(c || '').trim().toLowerCase().indexOf(prefixLc) === 0;
    function codeInCookie() {
      return String(cookie.get('discount_code') || '').split(',').some(isWelcomeCode);
    }
    function checkCartCode() {
      if (!window.fetch) return;
      fetch(routeRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (!cart) return;
          const codes = (cart.discount_codes || []).map((d) => d.code);
          const welcome = codes.some(isWelcomeCode);
          if (!welcome && !codes.some(isOtherPromo)) return;
          if (state.open && state.view === 'form' && state.trigger !== 'click') return; // never yank an open form
          if (welcome) markSubscribed();
          else { session.set(SKEY.promo, '1'); hideTeaser(); } // another promotion is on the cart (e.g. BLOOM25)
          state.autoDone = true;
        })
        .catch(() => { /* offline or blocked: the timers decide */ });
    }
    // A visit from one of our emails (UTM-tagged): the reader is already on the list, so no pop-up this session.
    const utmMedium = (url.searchParams.get('utm_medium') || '').toLowerCase();
    const utmSource = (url.searchParams.get('utm_source') || '').toLowerCase();
    if (utmMedium === 'email' || utmSource.indexOf('shopify_email') !== -1) session.set(SKEY.email, '1');
    const fromEmail = () => session.get(SKEY.email) === '1';
    /* A visitor who already holds ANOTHER promotion is not offered the 15% sign-up (Diego, 2026-10-11): the codes
       never combine, and a smaller offer on top of a bigger one only confuses. Example: the BLOOM25 postcard — its QR
       goes through /discount/BLOOM25 (readable discount_code cookie, code on the cart) with utm_medium=print.
       Session-scoped: no auto pop-up and no "15% off" tab; the footer button still opens the form on request. */
    if (utmMedium === 'print') session.set(SKEY.promo, '1');
    const isOtherPromo = (c) => !!String(c || '').trim() && !isWelcomeCode(c);
    const otherPromoInCookie = () => String(cookie.get('discount_code') || '').split(',').some(isOtherPromo);
    const holdsOtherPromo = () => session.get(SKEY.promo) === '1' || otherPromoInCookie();
    function markDismissed() { local.set(KEY.dis, String(Date.now()), cfg.days || 7); }

    /* ---------- views ---------- */
    function setView(view) {
      state.view = view;
      $$('[data-evw-view]').forEach((el) => {
        const on = el.getAttribute('data-evw-view') === view;
        el.hidden = !on;
        if (on) {
          dialog.setAttribute('aria-labelledby', el.getAttribute('data-title'));
          dialog.setAttribute('aria-describedby', el.getAttribute('data-text'));
        }
      });
    }
    function setGreeting(name) {
      const g = $('[data-evw-greeting]');
      if (g) g.textContent = name ? ', ' + name : '';
    }

    /* ---------- open / close ---------- */
    const styled = () => window.getComputedStyle(root).position === 'fixed';
    function whenStyled(fn) {
      if (styled()) return fn();
      if (cssLink) cssLink.media = 'all';
      let tries = 0;
      const tick = () => { if (styled() || ++tries > 40) fn(); else setTimeout(tick, 50); };
      tick();
    }

    function lockScroll(on) {
      const html = document.documentElement;
      if (on) {
        const gap = window.innerWidth - html.clientWidth;
        if (gap > 0) document.body.style.paddingRight = gap + 'px';
        html.classList.add('evw-lock');
      } else {
        html.classList.remove('evw-lock');
        document.body.style.paddingRight = '';
      }
    }

    function open(view, opts) {
      opts = opts || {};
      setView(view || 'form');
      if (state.open || state.opening) return;
      state.opening = true;
      whenStyled(() => {
        state.opening = false;
        state.open = true;
        state.trigger = opts.trigger || state.trigger;
        state.lastFocus = opts.returnFocus || (document.activeElement !== document.body ? document.activeElement : null);
        // Opened by the page itself or by an ad link: counts as seen, so a reload or the next page does not bring it back.
        if (opts.shown && !designMode && !subscribed()) markDismissed();
        dialog.setAttribute('aria-modal', 'true');
        hideTeaser(true);
        loadImage();
        mountDisclaimer(true);
        root.hidden = false;
        document.documentElement.classList.add('evw-open');
        lockScroll(true);
        fitViewport();
        // Grace (automatic opens only, opts.grace): for GRACE ms the dimmed backdrop takes no taps (CSS .is-arming), so
        // a tap the shopper already aimed at the page lands there; a tap on Add to cart also closes it (see
        // watchShopper). The card takes taps from the first frame. Then the page behind goes inert. Opens the shopper
        // asked for (the tab, ?welcome) are modal at once.
        const seq = ++state.armSeq;
        state.arming = !designMode && !!opts.grace;
        root.classList.toggle('is-arming', state.arming);
        if (state.arming) {
          setTimeout(() => {
            if (seq !== state.armSeq || !state.open) return;
            state.arming = false;
            root.classList.remove('is-arming');
            setInert(true);
          }, GRACE);
        } else setInert(true);
        void root.offsetWidth; // commit the closed styles so the transition runs
        root.classList.add('is-open');
        // Focus moves into the dialog (Escape and the focus trap work at once). Only a click on a computer puts it in
        // the first field; on phones focusing the dialog itself never raises the keyboard.
        const firstField = form && Array.prototype.find.call(form.querySelectorAll('input:not([type="hidden"])'), (el) => el.getClientRects().length > 0);
        const target = opts.focusInput && finePointer.matches && state.view === 'form' ? firstField || email : dialog;
        setTimeout(() => { if (state.open && target) target.focus({ preventScroll: true }); }, 60);
        if (state.view === 'form') setTimeout(() => { if (state.open) warmCaptcha(); }, 400);
        document.addEventListener('keydown', onKey, true);
      });
    }

    function close(reason) {
      if (!state.open) return;
      state.open = false;
      state.arming = false;
      state.armSeq++;
      const focusInside = root.contains(document.activeElement);
      root.classList.remove('is-open', 'is-arming', 'is-kb');
      document.removeEventListener('keydown', onKey, true);
      setInert(false);
      // A tap on Add to cart closed it: give the page its scroll back now, the tap is already on its way to the button.
      if (reason === 'tuck') lockScroll(false);
      const done = () => { if (!state.open) { root.hidden = true; root.classList.remove('is-vv'); lockScroll(false); mountDisclaimer(false); document.documentElement.classList.remove('evw-open'); } };
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(done, reduce ? 160 : 460);
      const back = state.lastFocus;
      const fromTeaser = !!(teaser && back && teaser.contains(back));
      // Every close of the form starts the cooldown; the "15% off" tab takes over.
      if (!designMode && !state.preview && state.view === 'form' && !subscribed() && reason !== 'editor') {
        markDismissed();
        if (fromTeaser) showTeaser(); else later(showTeaser, 900);
      }
      state.preview = false;
      if (reason === 'tuck' && !focusInside) return; // focus follows the shopper's tap
      if (back && document.contains(back) && back.getClientRects().length && typeof back.focus === 'function') back.focus({ preventScroll: true });
      else if (reason !== 'editor') {
        const a = document.activeElement;
        if (!a || a === document.body || root.contains(a)) focusPage();
      }
    }

    function onKey(e) {
      if (!state.open) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); e.stopPropagation(); close('esc'); return; }
      if (e.key !== 'Tab') return;
      const items = Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), (el) => el.getClientRects().length > 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && (active === first || active === dialog)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }

    /* Phones: keep the card inside the part of the screen the shopper can see. With the keyboard up the visible area
       (visualViewport) is much shorter than the window and may be scrolled; the overlay follows it (.is-vv). Once the
       shopper is typing (or the visible area shrinks) the card goes compact for the rest of this open (.is-kb): no
       photo band, eyebrow or text, pinned to the top, so the field and the button stay in view. It does not switch
       back on blur: a layout jump under the finger that is reaching for the button could make that tap miss. */
    function fitViewport() {
      if (!state.open || !phone.matches) return;
      const vv = window.visualViewport;
      const useVv = !!(vv && Math.abs((vv.scale || 1) - 1) < 0.05); // pinch-zoom is not a keyboard
      root.classList.toggle('is-vv', useVv);
      if (useVv) {
        root.style.setProperty('--evw-vvh', Math.round(vv.height) + 'px');
        root.style.setProperty('--evw-vvt', Math.round(vv.offsetTop) + 'px');
      }
      const a = document.activeElement;
      const typing = !!(a && dialog.contains(a) && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName));
      if (typing || (useVv && window.innerHeight - vv.height > 150)) root.classList.add('is-kb');
    }

    /* The photo sits in a <template>: mounted shortly before the timer is due, when the "15% off" tab appears, or when
       the pop-up opens. Phones get the short band (or nothing, if "Show image on mobile" is off). */
    let imageWarm = false;
    function loadImage() {
      if (imageWarm) return;
      const media = $('.evw__media');
      const tpl = $('template[data-evw-img]');
      if (!media || !tpl) return;
      if (media.classList.contains('evw__media--desktop') && phone.matches) return;
      imageWarm = true;
      media.appendChild(tpl.content.cloneNode(true));
    }

    /* ---------- offer tab ---------- */
    function teaserAllowed() {
      return !!(teaser && cfg.teaser && !designMode && !subscribed() && dismissedAt() && inCooldown() && local.get(KEY.teaser) !== String(dismissedAt()));
    }
    function showTeaser(force) {
      if (!teaser || (!force && !teaserAllowed()) || state.open) return;
      teaser.hidden = false;
      runGuard(); // never appear on top of an Add to cart
      loadImage(); // a tap on the tab opens the pop-up with the photograph: have it ready
      void teaser.offsetWidth;
      teaser.classList.add('is-visible');
    }

    /* ---------- out of the shopper's way ---------- */
    const overlaps = (a, b, m) => a.width > 0 && a.height > 0 && a.left < b.right + m && a.right > b.left - m && a.top < b.bottom + m && a.bottom > b.top - m;
    const buyBoxes = () => Array.prototype.filter.call(document.querySelectorAll(BUYBOX), (el) => el.getClientRects().length > 0);
    // Phones and tablets: the product form (or the add-ons with it) anywhere on screen means the shopper is at the buy box.
    // Computers: the buy box sits beside the gallery and is usually in view, so it counts while the pointer rests on it
    // (or keyboard focus is in it). A pointer that left the window (exit intent) is not at the buy box.
    function nearBuyBox() {
      const boxes = buyBoxes();
      if (!boxes.length) return false;
      if (phone.matches || !finePointer.matches) {
        const zone = { left: 0, right: window.innerWidth, top: -NEAR, bottom: window.innerHeight + NEAR };
        return boxes.some((el) => overlaps(el.getBoundingClientRect(), zone, 0));
      }
      const a = document.activeElement;
      if (a && boxes.some((el) => el.contains(a))) return true;
      if (state.px < 0) return false;
      const pt = { left: state.px, right: state.px, top: state.py, bottom: state.py };
      return boxes.some((el) => overlaps(el.getBoundingClientRect(), pt, NEAR));
    }
    function overAddToCart(r) {
      if (!r.width || !r.height || typeof document.elementsFromPoint !== 'function') return false;
      const xs = [r.left + 2, r.left + r.width / 2, r.right - 2];
      const ys = [r.top + 2, r.top + r.height / 2, r.bottom - 2];
      for (let i = 0; i < xs.length; i++) {
        for (let j = 0; j < ys.length; j++) {
          if (xs[i] < 0 || ys[j] < 0 || xs[i] >= window.innerWidth || ys[j] >= window.innerHeight) continue;
          const under = document.elementsFromPoint(xs[i], ys[j]);
          if (under.some((el) => !teaser.contains(el) && !root.contains(el) && el.closest && el.closest(KEEP_CLEAR))) return true;
        }
      }
      return false;
    }
    // The "15% off" tab hides while an Add to cart or the product price is under it.
    function runGuard() {
      if (teaser && !teaser.hidden) teaser.classList.toggle('is-tucked', overAddToCart(teaser.getBoundingClientRect()));
    }
    let guardFrame = 0;
    function guard() {
      if (guardFrame) return;
      guardFrame = window.requestAnimationFrame(() => { guardFrame = 0; runGuard(); });
    }
    function watchShopper() {
      const touched = () => { state.lastTouch = Date.now(); };
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => listen(document, type, touched, { capture: true, passive: true }));
      listen(window, 'scroll', () => { touched(); guard(); }, { passive: true });
      listen(window, 'resize', () => { guard(); fitViewport(); }, { passive: true });
      if (window.visualViewport) {
        listen(window.visualViewport, 'resize', fitViewport, { passive: true });
        listen(window.visualViewport, 'scroll', fitViewport, { passive: true });
      }
      listen(dialog, 'focusin', fitViewport);
      // Computers: where the mouse rests ("at the buy box" is the pointer on it).
      if (finePointer.matches) listen(document, 'pointermove', (e) => { if (e.pointerType === 'mouse') { state.px = e.clientX; state.py = e.clientY; } }, { passive: true });
      // Shopping: start the quiet period. In the grace moments the backdrop takes no taps, so a tap on Add to cart
      // reaches the button; this closes the pop-up in the same instant and the tap adds (one tap, nothing swallowed).
      const shopping = (e) => {
        const el = e.target;
        if (!el || !el.closest || root.contains(el) || !el.closest(BUY + ',' + ATC)) return;
        state.lastBuy = Date.now();
        if (state.open && state.arming) close('tuck');
      };
      ['pointerdown', 'focusin', 'change', 'submit'].forEach((type) => listen(document, type, shopping, { capture: true }));
      // The card takes taps from its first frame, so in the grace moments a finger already on its way to an Add to cart
      // that the card now covers lands on the card. If an Add to cart sits right under that spot, the tap was meant for
      // it: the pop-up tucks away and the tap adds (the x and "No thanks" still close). The click reaches the button on
      // its own (the card no longer takes taps), or is handed to it here if the browser had aimed it at the card; once.
      let fwd = null;
      const atcUnder = (x, y) => {
        if (typeof document.elementsFromPoint !== 'function') return null;
        const under = document.elementsFromPoint(x, y).find((el) => !root.contains(el) && !(teaser && teaser.contains(el)));
        return under && under.closest ? under.closest(ATC) : null;
      };
      const handOver = () => { if (!fwd || fwd.done) return; fwd.done = true; fwd.handed = true; fwd.btn.click(); };
      listen(dialog, 'pointerdown', (e) => {
        if (!state.open || !state.arming || !e.isPrimary || (e.target.closest && e.target.closest('[data-evw-close]'))) return;
        const btn = atcUnder(e.clientX, e.clientY);
        if (!btn) return;
        fwd = { btn: btn, t: Date.now(), done: false };
        state.lastBuy = Date.now();
        close('tuck');
      }, { capture: true });
      listen(document, 'pointerup', () => { if (fwd && !fwd.done) { const f = fwd; setTimeout(() => { if (fwd === f) handOver(); }, 400); } }, { capture: true });
      listen(document, 'pointercancel', () => { if (fwd && !fwd.done) fwd = null; }, { capture: true }); // it became a scroll
      listen(document, 'click', (e) => {
        if (!fwd || !e.isTrusted) return;
        if (Date.now() - fwd.t > 1500) { fwd = null; return; }
        const onBtn = fwd.btn.contains(e.target);
        if (!fwd.done) {
          if (onBtn) { fwd.done = true; return; } // the button got the tap itself
          e.preventDefault(); e.stopPropagation(); handOver(); return; // the tap's click was aimed at the card: hand it over
        }
        if (onBtn && fwd.handed) { e.preventDefault(); e.stopPropagation(); } // a late click after the hand-over: never twice
      }, { capture: true });
      const bar = document.querySelector('.ev-sticky-atc');
      if (bar && 'MutationObserver' in window) {
        const mo = new MutationObserver(() => guard());
        mo.observe(bar, { attributes: true, attributeFilter: ['class'] });
        if (ac) ac.signal.addEventListener('abort', () => mo.disconnect());
      }
    }
    function hideTeaser(temporary) {
      if (!teaser || teaser.hidden) return;
      teaser.classList.remove('is-visible');
      setTimeout(() => { if (!teaser.classList.contains('is-visible')) teaser.hidden = true; }, temporary ? 0 : 380);
    }

    /* ---------- success: tracking ---------- */
    function track(source) {
      const detail = { code: cfg.code, source: source || 'popup', page: window.location.pathname, language: lang };
      const publish = () => {
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            window.Shopify.analytics.publish('everla:welcome_signup', detail);
            return true;
          }
        } catch (e) { /* analytics unavailable */ }
        return false;
      };
      if (!publish()) {
        const retry = () => setTimeout(publish, 600);
        if (document.readyState === 'complete') retry(); else window.addEventListener('load', retry, { once: true });
      }
      try { if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: 'everla_welcome_signup' }, detail)); } catch (e) { /* no GTM */ }
      document.dispatchEvent(new CustomEvent('everla:welcome-signup', { detail: detail }));
    }

    /* ---------- after Shopify's redirect ---------- */
    function readPending() {
      try {
        const p = JSON.parse(session.get(SKEY.pending) || 'null');
        return p && Date.now() - p.t < 30 * 60 * 1000 ? p : null;
      } catch (e) { return null; }
    }

    function cleanUrl(keys, dropHash) {
      const run = () => {
        const u = new URL(window.location.href);
        const drop = Array.from(u.searchParams.keys()).filter((k) => keys.indexOf(k) !== -1 || (keys.indexOf('contact') !== -1 && k.indexOf('contact[') === 0));
        drop.forEach((k) => u.searchParams.delete(k));
        if (dropHash) u.hash = '';
        try { window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash); } catch (e) { /* ignore */ }
      };
      // after load: Shopify's captcha bootstrap reads form_type/form_key on DOMContentLoaded
      if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
    }

    function handleReturn() {
      const params = url.searchParams;
      const hash = window.location.hash;
      const posted = params.get('customer_posted') === 'true';
      const pending = readPending();
      const footerNote = !!(pending && pending.form === 'footer');
      const ours = hash === '#' + cfg.formId || (!!pending && !footerNote && (!hash || hash === '#' + cfg.formId));
      const otherForm = hash && hash !== '#' + cfg.formId && /^#[A-Za-z][\w-]*$/.test(hash) && document.getElementById(hash.slice(1)) && document.getElementById(hash.slice(1)).tagName === 'FORM';
      const news = !!(otherForm && document.getElementById(hash.slice(1)).matches(NEWS));
      const serverErrors = !!$('[data-evw-error]');
      const customerReturn = params.get('form_type') === 'customer';

      // form.errors is shared by every customer form on the page: errors from the footer form are not ours.
      if (serverErrors && !ours) clearServerError();

      if (posted && ours) {
        session.del(SKEY.pending);
        markSubscribed();
        setGreeting(pending && pending.name);
        open('success', { trigger: 'signup' });
        // One Lead per real submit: the submit handler left a pending note in this tab. A revisited or hand-made
        // ?customer_posted URL has none, so it shows the success view but is not counted (unless storage is blocked).
        const once = pending ? window.location.pathname + '|' + pending.t : null;
        if (once ? session.get(SKEY.handled) !== once : !session.works()) {
          if (once) session.set(SKEY.handled, once);
          track(pending ? pending.trigger : 'popup');
        }
        cleanUrl(FORM_PARAMS, true);
        return true;
      }
      if (otherForm && (posted || customerReturn)) {
        // The footer (or another) newsletter form. Success: remember and stay closed. Anything else
        // (duplicate, captcha re-check, error): the visitor just tried to subscribe, so no auto-open on this page.
        if (posted) markSubscribed();
        if (news) {
          // The footer "Join" form: a real sign-up is a Lead too, counted exactly like the pop-up's (one per submit).
          if (posted && (footerNote || !session.works())) {
            const once = footerNote ? window.location.pathname + '|' + pending.t : null;
            if (!once || session.get(SKEY.handled) !== once) {
              if (once) session.set(SKEY.handled, once);
              track('footer');
            }
          }
          if (footerNote) session.del(SKEY.pending); // a captcha re-check or an error: the re-submit writes a new note
          cleanUrl(FORM_PARAMS, false); // keep #EvNewsletter so the "You're on the list" line stays in view
        }
        state.autoDone = true;
        return false;
      }
      if (!posted && ours && (serverErrors || params.has('form_key'))) {
        // Shopify refused the post, or sent it back for a captcha re-check (its bootstrap refills the fields
        // from form_key on DOMContentLoaded). Nothing was created: show the form again, remember nothing.
        session.del(SKEY.pending);
        open('form', { trigger: 'retry' });
        setTimeout(() => { if (email) email.focus({ preventScroll: true }); }, 120);
        cleanUrl(FORM_PARAMS, hash === '#' + cfg.formId);
        return true;
      }
      if (!posted && ours && customerReturn) {
        // Accepted without customer_posted: usually an email that is already a customer. An uncertain signal,
        // so it gets the normal cooldown, not the two-year "subscribed" suppression.
        session.del(SKEY.pending);
        markDismissed();
        open('already', { trigger: 'duplicate' });
        cleanUrl(FORM_PARAMS, hash === '#' + cfg.formId);
        return true;
      }
      return false;
    }

    function clearServerError() {
      const err = $('[data-evw-error]');
      if (err) err.remove();
      if (email) { email.removeAttribute('aria-invalid'); email.removeAttribute('aria-describedby'); }
    }

    /* ---------- triggers ---------- */
    function blockedByPage() {
      if (document.hidden) return true;
      const busy = document.querySelector('.ev-cart.is-open,.ev-modal.is-open,.ev-drawer.is-open,.ev-search.is-open,.ev-filters.is-open,.ev-zoom.is-open');
      if (busy || document.body.classList.contains('ev-lock')) return true;
      const a = document.activeElement;
      return !!(a && a !== document.body && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !root.contains(a));
    }
    // Someone part-way through a form on the page (contact, quote, footer signup): never interrupt this page view.
    function midForm() {
      return Array.prototype.some.call(document.querySelectorAll(TYPING), (el) => !el.readOnly && !root.contains(el) && el.value !== '' && el.value !== el.defaultValue);
    }

    // How long to hold an automatic open (0 = now): shopping comes first.
    function notNow() {
      const now = Date.now();
      if (now - state.lastBuy < BUY_QUIET) return BUY_QUIET - (now - state.lastBuy) + 100;
      if (now - state.lastTouch < CALM) return CALM - (now - state.lastTouch) + 100;
      if (nearBuyBox()) return 3000;
      return 0;
    }

    let retryTimer = 0;
    function retry(trigger, ms) {
      if (retryTimer) return; // one pending re-check at a time, whichever trigger asked
      retryTimer = setTimeout(() => { retryTimer = 0; autoOpen(trigger); }, ms);
      timers.push(retryTimer);
    }

    function autoOpen(trigger) {
      if (state.autoDone || state.open) return;
      if (subscribed() || inCooldown() || midForm()) { state.autoDone = true; return; }
      if (blockedByPage()) { if (trigger !== 'exit') retry(trigger, 4000); return; }
      const wait = notNow();
      if (wait) { if (trigger !== 'exit') retry(trigger, wait); return; } // a leaving pointer is not worth a late card
      state.autoDone = true;
      open('form', { trigger: trigger, shown: true, grace: true });
    }

    function armTriggers() {
      if (!cfg.enabled || designMode || subscribed() || inCooldown()) return;
      const delay = Math.max(AUTO_MIN, (cfg.delay || 8) * 1000);
      later(() => autoOpen('timer'), delay);
      // Have the photograph ready when it opens (nothing is fetched for a visitor who leaves before then).
      later(() => { if (!state.autoDone && !subscribed() && !inCooldown()) loadImage(); }, Math.max(0, delay - PREWARM));

      let ticking = false;
      const depth = Math.min(Math.max(cfg.scroll || 45, 5), 95) / 100;
      listen(window, 'scroll', () => {
        if (ticking || state.autoDone) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && window.scrollY / max >= depth) {
            const wait = AUTO_MIN - (Date.now() - state.started);
            if (wait > 0) retry('scroll', wait); else autoOpen('scroll');
          }
        });
      }, { passive: true });

      if (cfg.exitIntent && finePointer.matches) {
        listen(document, 'mouseout', (e) => {
          if (e.relatedTarget || e.clientY > 0 || Date.now() - state.started < MIN_ENGAGED) return;
          autoOpen('exit');
        });
      }
    }

    /* ---------- wiring ---------- */
    listen(root, 'click', (e) => {
      if (e.target.closest('[data-evw-close]')) { e.preventDefault(); close('close'); return; }
      if (e.target.closest('[data-evw-retry]')) {
        // Keep the subscribed mark: a subscriber who opens the form again and then closes it must not get the
        // pop-up back on the next page. A new sign-up sets it again anyway.
        if (email) email.value = '';
        setView('form');
        setTimeout(() => { if (email) email.focus({ preventScroll: true }); }, 30);
      }
    });

    let busyTimer = 0;
    let waitBind = null;
    function setBusy(on, ms) {
      if (!submit) return;
      const label = submit.querySelector('[data-evw-t]') || submit;
      clearTimeout(busyTimer);
      submit.classList.toggle('is-busy', on);
      if (on) submit.setAttribute('aria-busy', 'true'); else submit.removeAttribute('aria-busy');
      label.textContent = t(on ? 'busy' : 'button');
      if (on) busyTimer = setTimeout(() => setBusy(false), ms || 12000);
    }

    listen(form, 'submit', () => {
      // No preventDefault: Shopify's captcha binder submits the form natively after adding its token.
      const name = nameInput && nameInput.offsetParent !== null ? nameInput.value.trim().slice(0, 40) : '';
      session.set(SKEY.pending, JSON.stringify({ t: Date.now(), name: name, trigger: state.trigger, lang: lang }));
      session.set(SKEY.lang, lang);
      if (!captchaOn() || bound()) { setBusy(true); return; }
      // Not bound yet: Shopify's bootstrap drops this submit. Finish binding, then send it once, for them.
      warmCaptcha();
      setBusy(true, 10000);
      if (waitBind || !('MutationObserver' in window)) return;
      waitBind = new MutationObserver(() => {
        if (!bound()) return;
        waitBind.disconnect();
        waitBind = null;
        if (!state.open || state.view !== 'form') return setBusy(false);
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submit); else form.submit();
      });
      waitBind.observe(form, { attributes: true, attributeFilter: ['data-hcaptcha-bound', 'data-recaptcha-bound'] });
      later(() => { if (waitBind) { waitBind.disconnect(); waitBind = null; setBusy(false); } }, 10000);
    });

    // The footer "Join" form: note the submit (capture phase, never preventDefault: Shopify's captcha owns the post)
    // so its ?customer_posted return can be counted once, like the pop-up's. A re-submit replaces the note.
    listen(document, 'submit', (e) => {
      const f = e.target;
      if (!f || !f.matches || !f.matches(NEWS) || root.contains(f)) return;
      session.set(SKEY.pending, JSON.stringify({ t: Date.now(), form: 'footer', trigger: 'footer', lang: lang }));
    }, { capture: true });

    listen(email, 'input', () => {
      if (email.getAttribute('aria-invalid') === 'true') { email.removeAttribute('aria-invalid'); const err = $('[data-evw-error]'); if (err) err.hidden = true; }
    });

    if (teaser) {
      listen(teaser, 'click', (e) => {
        if (!e.target.closest('[data-evw-teaser-close]')) return;
        const hadFocus = teaser.contains(document.activeElement);
        local.set(KEY.teaser, String(dismissedAt()), cfg.days || 7);
        hideTeaser();
        if (hadFocus) focusPage(); // the focused button is about to disappear
      });
    }

    /* ---------- boot ---------- */
    translate();
    session.set(SKEY.lang, lang);

    function boot() {
      if (designMode) return;
      watchShopper();
      const hash = window.location.hash;
      if (hash === '#welcome-success' || hash === '#welcome-already') {
        state.preview = true;
        open(hash.slice(1).replace('welcome-', ''), { trigger: 'preview' });
        return;
      }
      if (handleReturn()) return;
      if (cfg.enabled && (url.searchParams.has('welcome') || hash === '#welcome')) {
        if (subscribed()) { state.preview = true; open('already', { trigger: 'link' }); } // their code is in the welcome email
        else open('form', { trigger: url.searchParams.get('utm_source') || 'link', shown: true });
        state.autoDone = true;
        cleanUrl(['welcome'], hash === '#welcome');
        return;
      }
      if (codeInCookie()) { markSubscribed(); return; }
      if (fromEmail()) return;
      if (holdsOtherPromo()) { session.set(SKEY.promo, '1'); return; }
      if (teaserAllowed()) later(showTeaser, 1500);
      armTriggers();
      if (cfg.enabled && !subscribed()) checkCartCode();
    }
    boot();

    return {
      root: root,
      open: (view, opts) => { if (view === 'success' || view === 'already') state.preview = !state.open; open(view, opts); },
      close: close,
      showTeaser: () => showTeaser(true),
      editorShow(view) {
        if (view === 'teaser') { close('editor'); showTeaser(true); return; }
        hideTeaser(true);
        state.preview = true;
        open(view || 'form', { trigger: 'editor' });
      },
      editorHide() { close('editor'); hideTeaser(true); },
      openFromClick(el) {
        if (subscribed()) { state.preview = true; open('already', { trigger: 'click', returnFocus: el }); }
        else open('form', { trigger: 'click', returnFocus: el, focusInput: true });
      },
      destroy() {
        timers.forEach(clearTimeout);
        clearTimeout(busyTimer);
        if (waitBind) { waitBind.disconnect(); waitBind = null; }
        if (ac) ac.abort();
        document.removeEventListener('keydown', onKey, true);
        setInert(false);
        if (state.open) lockScroll(false);
      }
    };
  }

  function init() {
    if (app) { app.destroy(); app = null; }
    const root = document.querySelector('[data-evw]');
    if (root) app = create(root);
  }

  /* Delegated so buttons added later (e.g. in other sections) work too. */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-everla-welcome-open]');
    if (!trigger || !app) return;
    e.preventDefault();
    app.openFromClick(trigger);
  });

  /* Theme editor: preview on select, re-init when settings change. */
  if (designMode) {
    const mine = (e) => app && e.detail && e.detail.sectionId === app.root.getAttribute('data-section-id');
    const readState = () => {
      try { return JSON.parse(document.querySelector('[data-evw-config]').textContent).editorState; } catch (e) { return 'form'; }
    };
    document.addEventListener('shopify:section:load', (e) => { if (e.detail && String(e.detail.sectionId).indexOf('everla-welcome') !== -1) init(); });
    document.addEventListener('shopify:section:unload', (e) => { if (mine(e)) { app.destroy(); app = null; } });
    document.addEventListener('shopify:section:select', (e) => { if (mine(e)) app.editorShow(readState()); });
    document.addEventListener('shopify:section:deselect', (e) => { if (mine(e)) app.editorHide(); });
  }

  window.EverlaWelcome = {
    loaded: true,
    open: (view) => app && app.open(view || 'form', { trigger: 'api', grace: true }),
    close: () => app && app.close('api')
  };

  init();
})();
